import {
  fetchAccessToken,
  fetchUserContext,
} from "@origin-digital/event-dispatcher";
import { config } from "../config";
import { HolderDetails } from "../screens/JointAccountHolder/validation";
import { resolveJointAccountHolderContext } from "./jointAccountHolderContext";

/** Avoid daxi's auth-error telemetry, which includes the full request body. */
export const submitJointAccountHolder = async (
  accountNumbers: string[],
  details: HolderDetails
): Promise<void> => {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout>;
  try {
    await Promise.race([
      (async () => {
        const context = await fetchUserContext({ skipCache: true });
        if (controller.signal.aborted) throw new Error("Context unavailable");
        const { accountIds: requested, authorName } =
          resolveJointAccountHolderContext(context, accountNumbers);
        const token = await fetchAccessToken({ environment: config.env });
        if (!token?.accessToken || controller.signal.aborted)
          throw new Error("Authentication unavailable");
        const response = await fetch(
          `${config.profileBffUrl}/v1/joint-account-holder`,
          {
            method: "POST",
            redirect: "error",
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token.accessToken}`,
            },
            body: JSON.stringify({
              accountIds: requested,
              authorName,
              firstName: details.firstName,
              lastName: details.lastName,
              dateOfBirth: details.dateOfBirth.split("/").reverse().join("-"),
              mobileNumber: details.mobileNumber,
              email: details.email,
            }),
          }
        );
        if (response.status !== 201) throw new Error("Unconfirmed response");
        const body = await response.json();
        const result = body?.data;
        if (
          !requested.length ||
          !result ||
          result.targeted !== requested.length ||
          result.created !== requested.length ||
          !Array.isArray(result.results) ||
          result.results.length !== requested.length ||
          result.results.some(
            (item: { accountId: string; status: string } | null) =>
              !item ||
              typeof item.accountId !== "string" ||
              !requested.includes(item.accountId) ||
              item.status !== "CREATED"
          ) ||
          new Set(
            result.results.map((item: { accountId: string }) => item.accountId)
          ).size !== requested.length
        )
          throw new Error("Invalid outcome");
      })(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => {
          controller.abort();
          reject(new Error("Unknown outcome"));
        }, 30000);
      }),
    ]);
  } catch {
    throw new Error("Unable to confirm joint account holder submission");
  } finally {
    clearTimeout(timeout!);
  }
};
